import { knowledgeTreeService } from "./knowledgeTreeService";

// Types for Dialogflow integration
export interface DialogflowConfig {
  projectId: string;
  sessionId: string;
  languageCode?: string;
  accessToken?: string;
}

export interface DialogflowResponse {
  queryResult: {
    queryText: string;
    parameters: Record<string, any>;
    intent: {
      displayName: string;
      confidence: number;
    };
    fulfillmentText: string;
    fulfillmentMessages: Array<{
      text: {
        text: string[];
      };
    }>;
  };
}

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  intent?: string;
  confidence?: number;
  hasLoginLink?: boolean;
}

class DialogflowService {
  private config: DialogflowConfig;
  private baseUrl: string;

  constructor(config: DialogflowConfig) {
    this.config = {
      languageCode: "en",
      ...config,
    };

    // Dialogflow REST API base URL
    this.baseUrl = `https://dialogflow.googleapis.com/v2/projects/${this.config.projectId}/agent/sessions/${this.config.sessionId}:detectIntent`;
  }

  /**
   * Send a message to Dialogflow and get a response
   *
   * ⚠️ SECURITY WARNING: This implementation sends credentials from the browser.
   * For production use, implement a backend proxy instead.
   * See docs/SECURITY_ANALYSIS.md for secure alternatives.
   */
  async sendMessage(message: string): Promise<DialogflowResponse> {
    try {
      // For browser compatibility, we'll use a simplified approach
      // In production, you should implement proper authentication
      const requestBody = {
        queryInput: {
          text: {
            text: message,
            languageCode: this.config.languageCode,
          },
        },
      };

      const response = await fetch(this.baseUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            this.config.accessToken || "your-access-token"
          }`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `Dialogflow API error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return data as DialogflowResponse;
    } catch (error) {
      console.error("Dialogflow API error:", error);
      throw new Error("Failed to get response from AI assistant");
    }
  }

  /**
   * Generate a fallback response when Dialogflow is unavailable
   * Uses the knowledge tree for structured conversational flows
   */
  generateFallbackResponse(userMessage: string): {
    response: string;
    followUpQuestions?: string[];
    context: any;
  } {
    const result = knowledgeTreeService.processMessage(userMessage);
    return result;
  }

  /**
   * Process the Dialogflow response and extract relevant information
   */
  processResponse(response: DialogflowResponse): {
    text: string;
    intent: string;
    confidence: number;
    parameters: Record<string, any>;
  } {
    const { queryResult } = response;

    return {
      text:
        queryResult.fulfillmentText ||
        "I apologize, but I didn't understand that. Could you please rephrase your question?",
      intent: queryResult.intent?.displayName || "Default Fallback Intent",
      confidence: queryResult.intent?.confidence || 0,
      parameters: queryResult.parameters || {},
    };
  }

  /**
   * Create a new session ID for the conversation
   */
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export a singleton instance
export const dialogflowService = new DialogflowService({
  projectId: process.env.REACT_APP_DIALOGFLOW_PROJECT_ID || "your-project-id",
  sessionId: "default-session",
});

export default DialogflowService;
